import React, { useEffect, useState } from 'react';
import { GeoJSON, MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MapViewUpdater from './MapViewUpdater';
import { useErrorToast } from '../../../hooks/useToast.jsx';

const continentDefaults = {
    africa: { center: [0, 20], zoom: 3 },
    asia: { center: [34, 100], zoom: 3 },
    europe: { center: [54, 15], zoom: 4 },
    'north america': { center: [40, -100], zoom: 3 },
    'south america': { center: [-15, -60], zoom: 3 },
    oceania: { center: [-25, 135], zoom: 3 }
};

const CountryMap = ({ searchTerm, selectedContinent, selectedCountry, goBackToContinent }) => {
    const [geoData, setGeoData] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const [fallbackView, setFallbackView] = useState(null);

    const computeAreaFromBounds = (bounds) => {
        const latDiff = Math.abs(bounds.getNorthEast().lat - bounds.getSouthWest().lat);
        const lngDiff = Math.abs(bounds.getNorthEast().lng - bounds.getSouthWest().lng);
        return latDiff * lngDiff;
    };

    useEffect(() => {
        if (!selectedContinent || !selectedCountry) return;
        setIsFetching(true);
        const slug = selectedContinent.toLowerCase().replace(/\s+/g, '_');
        fetch(`/data/continent/${slug}.json`)
            .then((res) => res.json())
            .then((data) => {
                data.features = data.features.filter((feature) => feature.geometry);
                data.features = data.features.map((feature) => {
                    const props = feature.properties;
                    const possibleNames = [props.NAME, props.name, props.BRK_NAME];
                    const matchedName = possibleNames.find(
                        (name) => name && selectedCountry && name.toLowerCase() === selectedCountry.toLowerCase()
                    );
                    feature.properties.countryName = matchedName || possibleNames.find((name) => !!name) || 'Unknown';
                    return feature;
                });

                const matchingFeatures = data.features.filter((feature) => {
                    return feature.properties.countryName.toLowerCase() === selectedCountry.toLowerCase();
                });

                if (matchingFeatures.length === 0) {
                    console.warn(`No geoJSON feature found for ${selectedCountry} in ${slug}.json`);
                    useErrorToast(`${selectedCountry} not found on the map. Showing continent view instead.`);
                    const defaultView = continentDefaults[selectedContinent.toLowerCase()];
                    if (defaultView) {
                        setFallbackView(defaultView);
                    }
                    setGeoData(null);
                    setIsFetching(false);
                    goBackToContinent();
                    return;
                }

                let finalFeature = null;
                matchingFeatures.forEach((feature) => {
                    if (feature.geometry.type === 'MultiPolygon') {
                        let maxArea = 0;
                        feature.geometry.coordinates.forEach((polygonCoords) => {
                            const polyFeature = {
                                type: 'Feature',
                                geometry: {
                                    type: 'Polygon',
                                    coordinates: polygonCoords
                                },
                                properties: feature.properties
                            };
                            const layer = L.geoJSON(polyFeature);
                            const bounds = layer.getBounds();
                            const area = computeAreaFromBounds(bounds);
                            if (area > maxArea) {
                                maxArea = area;
                                finalFeature = polyFeature;
                            }
                        });
                    } else if (feature.geometry.type === 'Polygon') {
                        const layer = L.geoJSON(feature);
                        const bounds = layer.getBounds();
                        const area = computeAreaFromBounds(bounds);
                        if (!finalFeature) {
                            finalFeature = feature;
                        } else {
                            const currentLayer = L.geoJSON(finalFeature);
                            const currentBounds = currentLayer.getBounds();
                            const currentArea = computeAreaFromBounds(currentBounds);
                            if (area > currentArea) {
                                finalFeature = feature;
                            }
                        }
                    }
                });

                setGeoData({ type: 'FeatureCollection', features: [finalFeature] });
                setIsFetching(false);
            })
            .catch((err) => {
                console.error('Error fetching geoJSON:', err);
                setIsFetching(false);
            });
    }, [selectedContinent, selectedCountry, searchTerm, goBackToContinent]);

    const onEachFeature = (feature, layer) => {
        if (!feature.properties || !feature.properties.countryName) return;
        layer.bindPopup(feature.properties.countryName);
    };

    return (
        <MapContainer
            key='country'
            center={[54.526, 15.2551]}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            maxBounds={[
                [-85.05112878, -180],
                [85.05112878, 180]
            ]}
            maxBoundsViscosity={1.0}
            worldCopyJump={false}
            zoomControl={false}
        >
            <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                maxZoom={19}
                noWrap={true}
            />
            <ZoomControl position='topright' />
            {geoData && (
                <>
                    <GeoJSON
                        data={geoData}
                        onEachFeature={onEachFeature}
                        style={{
                            color: '#607D8B',
                            weight: 1,
                            fillColor: '#ECEFF1',
                            fillOpacity: 0.5
                        }}
                    />
                </>
            )}
            {(geoData || fallbackView) && (
                <MapViewUpdater geoJsonData={geoData} fallbackView={fallbackView} desiredZoom={5} />
            )}
            {isFetching && <div>Loading country data...</div>}
        </MapContainer>
    );
};

export default CountryMap;
